import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { explorerUrl, shortAddress } from '@/lib/format';
import type { Hex } from '@/lib/demo/types';
import { Check, Copy, ExternalLink } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Identity primitives: generated token avatars, address chips, copy affordance.
   ──────────────────────────────────────────────────────────────────────────── */

const SIZE_MAP = { xs: 20, sm: 28, md: 36, lg: 48, xl: 64 } as const;
export type TokenMarkSize = keyof typeof SIZE_MAP | number;

export interface TokenMarkProps {
  symbol: string;
  /** 0–360. Use `hueFromAddress()` so a token's mark is stable forever. */
  hue: number;
  glyph?: string;
  size?: TokenMarkSize;
  ring?: boolean;
  className?: string;
}

/**
 * Deterministic avatar. No network request, no uploaded image to moderate — the
 * hue comes from the token address so the same token always looks the same.
 */
export function TokenMark({
  symbol,
  hue,
  glyph,
  size = 'md',
  ring = true,
  className,
}: TokenMarkProps) {
  const px = typeof size === 'number' ? size : SIZE_MAP[size];
  const h = ((Math.round(hue) % 360) + 360) % 360;
  const h2 = (h + 46) % 360;
  const h3 = (h + 312) % 360;

  const fallback = (symbol || '?').replace(/^\$/, '').slice(0, 2).toUpperCase();
  const content = glyph || fallback;
  const isGlyph = Boolean(glyph);

  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-xl shadow-inner-line',
        ring && 'ring-1 ring-inset ring-fg/10',
        className,
      )}
      style={{
        width: px,
        height: px,
        // Conic core gives the mark a lit edge; the linear pass adds depth.
        backgroundImage: [
          `linear-gradient(160deg, hsl(${h} 92% 66% / 0.55), hsl(${h3} 78% 34% / 0.65))`,
          `conic-gradient(from 205deg at 32% 22%, hsl(${h} 94% 64%), hsl(${h2} 88% 52%), hsl(${h3} 82% 44%), hsl(${h} 94% 64%))`,
        ].join(', '),
      }}
    >
      <span
        className={cn('select-none leading-none', isGlyph ? 'font-sans' : 'font-mono font-bold')}
        style={{
          fontSize: isGlyph ? px * 0.5 : px * 0.36,
          letterSpacing: isGlyph ? undefined : '-0.02em',
          color: 'rgb(255 255 255 / 0.95)',
          textShadow: '0 1px 2px rgb(0 0 0 / 0.35)',
        }}
      >
        {content}
      </span>
    </span>
  );
}

/* ── CopyButton ───────────────────────────────────────────────────────────── */

export interface CopyButtonProps {
  value: string;
  /** Accessible name; defaults to "Copy". */
  label?: string;
  className?: string;
}

/** Copies to the clipboard and flips to a check for 1.4s. */
export function CopyButton({ value, label = 'Copy', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard is unavailable (insecure context / denied). Still confirm the
      // interaction rather than leaving the button looking broken.
    }
    setCopied(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1400);
  }, [value]);

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      className={cn(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors duration-150 ease-swift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70',
        copied ? 'text-up' : 'text-subtle hover:text-fg',
        className,
      )}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

/* ── AddressChip ──────────────────────────────────────────────────────────── */

export interface AddressChipProps {
  address: Hex;
  /** Handle or ENS name to show instead of the truncated address. */
  handle?: string;
  chainId?: number;
  copy?: boolean;
  link?: boolean;
  className?: string;
}

export function AddressChip({
  address,
  handle,
  chainId = 84532,
  copy = true,
  link = false,
  className,
}: AddressChipProps) {
  const shown = handle || shortAddress(address);
  return (
    <span
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1 rounded-md border border-line bg-elevated px-1.5 py-0.5 text-2xs text-muted',
        className,
      )}
    >
      <span className={cn('truncate', handle ? 'font-sans' : 'font-mono tabular-nums')}>
        {shown}
      </span>
      {copy ? <CopyButton value={address} label={`Copy address ${address}`} /> : null}
      {link ? (
        <a
          href={explorerUrl(chainId, 'address', address)}
          target="_blank"
          rel="noreferrer"
          aria-label={`View ${shown} on the block explorer`}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-subtle transition-colors duration-150 ease-swift hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70"
        >
          <ExternalLink size={12} />
        </a>
      ) : null}
    </span>
  );
}
